import { bindActionCreators } from '@reduxjs/toolkit';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Actions, LanguageLevel } from 'src/model/enum/enums';
import { LanguageLevels } from 'src/model/LanguageLevels';
import { Learning } from 'src/model/Learning';
import { LessonState } from 'src/model/Lessons';
import { Chl } from 'src/model/Scenario';
import { Word, WordType } from 'src/model/Word';
import { scenarioActions } from '../../store/slices/scenarioSlice';
import { RootState } from '../../store/store';
import { GetResourcesUrl } from '../../utils/URLUtils';
import { useLearning } from './useLearning';
import useProgress from './useProgress';
import useUser from './useUser';
import { useWords } from './useWords';

export const useScenario = (autoFetch: boolean = true) => {
    const dispatch = useDispatch();
    const { scenarios, scenarioQAs, scenarioRandomWords, isLoading, error } = useSelector((state: RootState) => state.scenario);
    const { learningInfo, updateLearningInfo } = useLearning();
    const { saveProgress } = useProgress();
    const ac = bindActionCreators(scenarioActions, dispatch);
    const { getWordByText } = useWords();
    const { userInfo } = useUser();
    const processedWordsRef = useRef<{ [key: string]: boolean }>({});

    const fetchScenarioData = useCallback(async () => {
        ac.setIsLoading(true);
        try {
            const [scenariosResponse, scenarioQAsResponse] = await Promise.all([
                fetch(GetResourcesUrl('Scenarios.json')),
                fetch(GetResourcesUrl('ScenarioQAs.json'))
            ]);

            if (!scenariosResponse.ok || !scenarioQAsResponse.ok) {
                throw new Error('Failed to fetch scenario data');
            }
            const scenariosData = await scenariosResponse.json();
            const scenarioQAsData = await scenarioQAsResponse.json();
            ac.setScenariosData(scenariosData);
            ac.setScenarioQAs(scenarioQAsData);
        } catch (err) {
            ac.setError(err instanceof Error ? err.message : 'Failed to fetch scenario data');
            console.error(err);
        } finally {
            ac.setIsLoading(false);
        }
    }, [ac]);


    const getAllScenarios = useCallback(async (limit?: number) => {
        if (!scenarios || !learningInfo?.Lessons || !scenarioQAs) return [];

        const challenges = learningInfo.Scenarios.Challenges || [];
        const completedContext = learningInfo.Scenarios.CompletedContext || [];
        const allScenarios: LessonState[] = [];


        for (const category of scenarios.categories) {
            for (const ctx of category.ctx) {
                if (limit && allScenarios.filter(l => l.IsCompleted === false).length >= limit) {
                    return allScenarios;
                }

                const questionId = `${category.ref}${ctx.ref}`;

                if (completedContext.includes(questionId)) {
                    allScenarios.push({
                        QuestionId: questionId,
                        Cat: category.cat,
                        Name: ctx.name,
                        CompletedCount: ctx.chl.length,
                        TotalCount: ctx.chl.length,
                        IsCompleted: true
                    });
                }
                else {

                    let completedCount = 0;

                    for (const chl of ctx.chl) {

                        const fullRef = `${questionId}${chl.ref}`
                        if (challenges.some(conv => conv.includes(fullRef))) {
                            completedCount++;
                        }
                    }

                    allScenarios.push({
                        QuestionId: questionId,
                        Cat: category.cat,
                        Name: ctx.name,
                        CompletedCount: completedCount,
                        TotalCount: ctx.chl.length,
                        IsCompleted: completedCount === ctx.chl.length
                    });

                }
            }
        }
        return allScenarios;
    }, [scenarios, learningInfo, scenarioQAs]);


    // const addScenario = useCallback((arrayName: ScenarioArrays, value: string) => {
    //     if (learningInfo) {
    //         const updatedArray = Array.from(new Set([...learningInfo.Scenarios[arrayName], value]));
    //         updateLearningInfo({
    //             Scenarios: {
    //                 ...learningInfo.Scenarios,
    //                 [arrayName]: updatedArray
    //             }
    //         });
    //     }
    // }, [learningInfo, updateLearningInfo]);

    const areAllChallengesCompleted = useCallback((ref: string, index: string, chl: Chl[]) => {
        return chl.every(c =>
            [...(learningInfo?.Scenarios.Challenges || []),
            `${ref}${index}`].includes(`${ref}${c.ref}`)
        );
    }, [learningInfo]);


    const addChallenge = useCallback((ref: string, index: string, chl: Chl[]) => {
        if (!learningInfo) return;

        const fullRef = `${ref}${index}`;
        const updatedChallenges = Array.from(new Set([...learningInfo.Scenarios.Challenges, fullRef]));
        const updates: Partial<Learning> = {
            Scenarios: {
                ...learningInfo.Scenarios,
                Challenges: updatedChallenges,
                CompletedContext: learningInfo.Scenarios.CompletedContext
            }
        };

        if (areAllChallengesCompleted(ref, index, chl)) {
            const updatedCompletedContext = Array.from(new Set([...learningInfo.Scenarios.CompletedContext, ref]));
            updates.Scenarios = {
                ...updates.Scenarios,
                CompletedContext: updatedCompletedContext,
                Challenges: updatedChallenges
            };
            saveProgress({
                Action: Actions.ScenarioCompleted,
                Value: ref
            });
        }

        updateLearningInfo(updates);
        saveProgress({
            Action: Actions.ScenarioChallengeAdded,
            Value: fullRef
        });
    }, [learningInfo, updateLearningInfo, saveProgress, areAllChallengesCompleted]);

    const addCompletedContext = useCallback((ref: string) => {
        if (!learningInfo) return;
        const updatedCompletedContext = Array.from(new Set([...learningInfo.Scenarios.CompletedContext, ref]));
        updateLearningInfo({
            Scenarios: {
                ...learningInfo.Scenarios,
                CompletedContext: updatedCompletedContext
            }
        });
        saveProgress({
            Action: Actions.ScenarioCompleted,
            Value: ref
        });
    }, [learningInfo, updateLearningInfo, saveProgress]);



    const Includes = useCallback((word: Word): boolean => {
        if (!learningInfo) return false;
        const { KnownWordIds, UnknownWordIds } = learningInfo;
        const langLevel = userInfo?.LanguageLevel || LanguageLevel.Beginner;

        if (KnownWordIds.includes(word.ID)) return true;
        if (UnknownWordIds.includes(word.ID)) return false;
        if (word.Type === WordType.Phrase) return false;

        return word.Rank < (LanguageLevels[langLevel as keyof typeof LanguageLevels].Ratio * 1000);
    }, [learningInfo, userInfo]);


    const getRandomWords = useCallback((words: string, updateRedux: boolean = true) => {
        // Create a unique key for this set of words
        const wordsKey = words;

        // If we've already processed these words and updateRedux is true, just return the current random words
        if (processedWordsRef.current[wordsKey] && updateRedux && scenarioRandomWords) {
            return scenarioRandomWords;
        }

        // If words are not loaded yet, return empty array
        if (!words || words.length === 0) {
            return [];
        }

        const wordsArray = words.split(',').map(w => w.trim());
        const processedWords =
            wordsArray.map(word =>
                getWordByText(word))
                .filter((word): word is Word =>
                    word !== undefined);

        // If no words were processed (likely because words aren't loaded yet), return empty array
        if (processedWords.length === 0) {
            return [];
        }

        // Get known words (max 3)
        const knownWords = processedWords
            .filter(word => learningInfo?.KnownWordIds.includes(word.ID))
            .sort(() => Math.random() - 0.5) // Randomize
            .slice(0, 3); // Take max 3

        // Get remaining words sorted by rank
        const remainingWords = processedWords
            .filter(word => !knownWords.includes(word))
            .sort((a, b) => {
                const aRank = Includes(a) ? (20000 - a.Rank) : a.Rank;
                const bRank = Includes(b) ? (20000 - b.Rank) : b.Rank;
                return aRank - bRank;
            });

        // Combine and take 8 words total
        const result = [...knownWords, ...remainingWords]
            .filter((word, index, self) => self.findIndex(w => w.ID === word.ID) === index) // Distinct
            .slice(0, 8);

        // Update Redux state with the random words only if updateRedux is true
        if (updateRedux) {
            ac.setScenarioRandomWords(result);
            // Mark these words as processed
            processedWordsRef.current[wordsKey] = true;
        }

        return result;
    }, [learningInfo, Includes, getWordByText, ac, scenarioRandomWords]);

    const setScenarioRandomWords = useCallback((words: Word[]) => {
        ac.setScenarioRandomWords(words);
    }, [ac]);

    useEffect(() => {
        if (autoFetch && (!scenarios || !scenarioQAs)) {
            fetchScenarioData();
        }
    }, [fetchScenarioData, scenarios, scenarioQAs, autoFetch]);

    return {
        scenarios,
        scenarioQAs,
        isLoading,
        error,
        fetchScenarioData,
        getAllScenarios,
        addChallenge,
        addCompletedContext,
        getRandomWords,
        setScenarioRandomWords,
        scenarioRandomWords
    };
};
